import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { FilterValues } from "../../../../data-model/filterValues";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { generateFilterPayloadForGenericAuthStatus } from "../utils/generateFilterPayloadForGenericAuthStatus";
import { useFilterPayload } from "./useFilterPayload";
import { GenericAuthStatusState } from "./useGenericAuthStatusTableData";
/**
 * Hook that manages infinite-scroll pagination for the generic auth status table.
 * Monitors the visible end index and automatically fetches the next page when the
 * user scrolls to the last row and more pages are available.
 *
 * @param validFilters - Currently applied filter values
 * @param initialDataFetchingExecuted - Whether the initial batch fetch has completed
 * @param searchTerm - Current search text
 * @param genericAuthStatusState - Current API response state with pagination metadata
 * @param setGenericAuthStatusState - State setter for API response metadata
 * @param setTableData - State setter for the processed table rows
 * @param tabId - Auth status tab identifier
 * @returns Object with endIndex (current visible end row) and its setter
 */
export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  initialDataFetchingExecuted: boolean,
  searchTerm: string,
  genericAuthStatusState: GenericAuthStatusState,
  setGenericAuthStatusState: React.Dispatch<
    React.SetStateAction<GenericAuthStatusState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<GenericAuthStatusTableData[]>
  >,
  tabId: string,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext(
    `${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`,
  );
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const [endIndex, setEndIndex] = useState(0);
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    tabId,
  );

  // Use refs to store latest values without triggering re-renders
  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setGenericAuthStatusStateRef = useRef(setGenericAuthStatusState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  // Keep refs updated
  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setGenericAuthStatusStateRef.current = setGenericAuthStatusState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setGenericAuthStatusState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (genericAuthStatusState.data?.hits?.length ?? 0) - 1 &&
      (genericAuthStatusState.data?.page ?? 0) <
        (genericAuthStatusState.data?.total_pages ?? 0) - 1
    );
  }, [
    endIndex,
    genericAuthStatusState.data?.hits?.length,
    genericAuthStatusState.data?.page,
    genericAuthStatusState.data?.total_pages,
  ]);

  const payloadFinal = useMemo(() => {
    if (filterPayload === null) {
      return generateFilterPayloadForGenericAuthStatus(
        validFilters,
        searchTerm,
        getOrgIdForFetchExternalWorklist(),
        user?.id ?? "",
        providerDetails?.FacilityId ?? "",
        providerDetails?.IsAdmin ?? false,
        tabId,
        sortByField,
        sortAscending,
      );
    }
    return filterPayload;
  }, [
    filterPayload,
    validFilters,
    searchTerm,
    user?.id,
    providerDetails?.FacilityId,
    providerDetails?.IsAdmin,
    tabId,
    sortByField,
    sortAscending,
  ]);

  const payloadFinalWithPage =
    useMemo<SearchForMedicalPaOrdersFromPostgresParams>(
      () => ({
        ...payloadFinal,
        page: (genericAuthStatusState.data?.page ?? 0) + 1,
      }),
      [payloadFinal, genericAuthStatusState.data?.page],
    );

  const currentPage = genericAuthStatusState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      authStatusOptions as AuthStatusOptionModel[],
      setGenericAuthStatusStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(
      `${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`,
      {
        lastBatchFetched: currentPage + 1,
      },
    );
  }, [payloadFinalWithPage, authStatusOptions, currentPage, tabId]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
